<template>
  <div class="mb-ov">
    <div v-if="!broadcast" class="mb-empty">Выберите или создайте трансляцию сверху — правки графики хранятся в ней.</div>

    <template v-else>
      <p class="mb-muted mb-small mb-ov__note">
        Правки — слой поверх данных Moonton: опрос их не затирает, и они действуют на всю серию, в том числе на
        следующие игры. Пустое поле — показываем данные из игры (они в подсказке). После «Сохранить» правки сразу в OBS.
      </p>

      <!-- эфир -->
      <section class="mb-ov__section">
        <h6 class="mb-ov__title">Эфир</h6>
        <div class="mb-ov__grid mb-ov__grid--3">
          <div class="mb-field">
            <label for="mb-ov-title">Заголовок эфира</label>
            <input id="mb-ov-title" v-model="draft.title" class="mb-input" placeholder="Например, Grand Final">
          </div>
          <div v-for="(c, i) in draft.casters" :key="'caster-' + i" class="mb-field">
            <label :for="'mb-ov-caster-' + i">Комментатор {{ i + 1 }}</label>
            <input :id="'mb-ov-caster-' + i" v-model="draft.casters[i]" class="mb-input" placeholder="Не задан">
          </div>
        </div>
      </section>

      <div v-if="!battle" class="mb-empty">Выберите матч во вкладке «Матчи и графика» — команды, игроки и герои берутся из него.</div>

      <template v-else>
        <!-- команды -->
        <section class="mb-ov__section">
          <h6 class="mb-ov__title">Команды</h6>
          <div class="mb-ov__grid mb-ov__grid--2">
            <div v-for="c in camps" :key="'camp-' + c.camp" class="mb-ov__card">
              <div class="mb-ov__card-head">
                <span :class="['mb-side', 'mb-side--' + c.camp]"></span>
                <b>{{ c.camp === 1 ? 'Синие' : 'Красные' }}</b>
                <span class="mb-muted mb-small mb-ellipsis">{{ campTeamName(c) }}</span>
              </div>
              <div v-if="!c.team_id" class="mb-alert mb-ov__locked">
                Сторона не привязана к нашей команде — сначала привяжите игру к матчу BIGPLAY.
              </div>
              <template v-else-if="draft.teams[c.team_id]">
                <div class="mb-ov__grid mb-ov__grid--2">
                  <div class="mb-field">
                    <label :for="'mb-ov-team-name-' + c.team_id">Название</label>
                    <input :id="'mb-ov-team-name-' + c.team_id"
                           v-model="draft.teams[c.team_id].name"
                           class="mb-input"
                           :placeholder="campTeamName(c)">
                  </div>
                  <div class="mb-field">
                    <label :for="'mb-ov-team-short-' + c.team_id">Короткое название</label>
                    <input :id="'mb-ov-team-short-' + c.team_id"
                           v-model="draft.teams[c.team_id].short_name"
                           class="mb-input"
                           :placeholder="campShortName(c)">
                  </div>
                </div>
                <div class="mb-field">
                  <label :for="'mb-ov-team-logo-' + c.team_id">Логотип</label>
                  <MbImageField :id="'mb-ov-team-logo-' + c.team_id"
                                v-model="draft.teams[c.team_id].logo"
                                :fallback="campLogoUrl(c) || ''"/>
                </div>
              </template>
            </div>
          </div>
        </section>

        <!-- игроки -->
        <section class="mb-ov__section">
          <h6 class="mb-ov__title">Игроки</h6>
          <p class="mb-muted mb-small mb-ov__hint">
            Moonton фото игроков не отдаёт: без правки и без привязанного профиля на эфире будет заглушка.
            Правка по MLBB ID игрока — она же действует в остальных играх серии.
          </p>
          <div class="mb-ov__grid mb-ov__grid--2">
            <div v-for="side in playerSides" :key="'players-' + side.camp" class="mb-ov__card">
              <div class="mb-ov__card-head">
                <span :class="['mb-side', 'mb-side--' + side.camp]"></span>
                <b>{{ side.title }}</b>
              </div>
              <div v-for="p in side.players" :key="'player-' + p.role_id" class="mb-ov__player">
                <div class="mb-ov__player-head">
                  <span class="mb-muted mb-small mb-mono">{{ p.role_id }}</span>
                  <span class="mb-small mb-ellipsis">{{ p.name || '—' }}</span>
                </div>
                <div class="mb-ov__player-fields">
                  <input v-model="draft.players[p.role_id].name"
                         class="mb-input mb-input--sm"
                         :placeholder="p.name || 'Ник'"
                         :aria-label="'Ник игрока ' + (p.name || p.role_id)">
                  <select v-model="draft.players[p.role_id].hero_id"
                          class="mb-select mb-input--sm"
                          :aria-label="'Герой игрока ' + (p.name || p.role_id)">
                    <option :value="null">{{ gameHeroLabel(p) }}</option>
                    <option v-for="h in heroList" :key="h.id" :value="h.id">{{ h.name }}</option>
                  </select>
                </div>
                <MbImageField v-model="draft.players[p.role_id].photo"
                              :fallback="userAvatar(p)"
                              placeholder="Фото: ссылка или загрузка"/>
              </div>
            </div>
          </div>
        </section>

        <!-- герои -->
        <section class="mb-ov__section">
          <h6 class="mb-ov__title">Картинки героев</h6>
          <p class="mb-muted mb-small mb-ov__hint">
            Подменяется везде, где встречается герой: пики, баны, «Игра» и «Итоги». Здесь герои этой игры.
          </p>
          <div v-if="!battleHeroes.length" class="mb-muted mb-small">В игре ещё нет банов и пиков.</div>
          <div v-else class="mb-ov__grid mb-ov__grid--2">
            <div v-for="h in battleHeroes" :key="'hero-' + h.id" class="mb-field">
              <label :for="'mb-ov-hero-' + h.id">{{ h.name }}</label>
              <MbImageField :id="'mb-ov-hero-' + h.id"
                            v-model="draft.heroes[h.id].image"
                            :fallback="h.avatar || ''"/>
            </div>
          </div>
        </section>
      </template>

      <div class="mb-ov__bar">
        <span v-if="dirty" class="mb-small">Есть несохранённые правки</span>
        <span v-else class="mb-muted mb-small">Всё сохранено</span>
        <span class="mb-spacer"></span>
        <button type="button" class="mb-btn mb-btn--ghost" :disabled="!dirty || saving" @click="reset">Отменить</button>
        <button type="button" class="mb-btn" :disabled="!dirty || saving" @click="save">
          {{ saving ? 'Сохраняем…' : 'Сохранить' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import MbImageField from '@/components/MlbbBroadcast/MbImageField.vue'
import {
  campLogoUrl,
  campPlayers,
  campShortName,
  campTeamName,
  heroAvatarUrl,
  heroCatalog,
  logoUrl,
  playerHero,
} from '@/common/mlbbBroadcast'
import {draftFromOverrides, emptyEntity, hasChanges, overridesPatch} from '@/common/mlbbBroadcastOverrides'

/**
 * Правки режиссёра в трансляции: заголовок, комментаторы, команды, игроки, картинки героев.
 * Черновик живёт локально, «Сохранить» шлёт только изменения (снятые поля — null).
 * Подсказки в полях — сырые данные игры (без правок), чтобы было видно, что покажется без правки.
 */
export default {
  name: 'MbOverridesTab',
  components: {MbImageField},
  props: {
    broadcast: {type: Object, default: null},
    // сырой источник (без display)
    battle: {type: Object, default: null},
    saving: {type: Boolean, default: false},
  },
  data() {
    return {draft: draftFromOverrides(null)}
  },
  computed: {
    saved() {
      return this.broadcast?.overrides || null
    },
    // трансляция присылает объект раз в секунду — сравниваем по содержимому
    savedKey() {
      return `${this.broadcast?.id || ''}:${JSON.stringify(this.saved || {})}`
    },
    patch() {
      return overridesPatch(this.saved, this.draft)
    },
    dirty() {
      return hasChanges(this.patch)
    },
    camps() {
      return [1, 2].map((n) => (this.battle?.camps || []).find((c) => c.camp === n) || {camp: n, team_id: null})
    },
    playerSides() {
      return [1, 2].map((n) => ({
        camp: n,
        title: campTeamName(this.camps[n - 1]),
        players: campPlayers(this.battle, n).filter((p) => p.role_id && this.draft.players[p.role_id]),
      }))
    },
    heroList() {
      return heroCatalog.list
    },
    // герои игры с нашим id: пики, баны и выбранные в правках
    battleHeroes() {
      const map = {}
      const add = (h) => {
        if (h?.id && !map[h.id]) map[h.id] = {id: h.id, name: h.name, avatar: heroAvatarUrl(h.avatar)}
      }
      const b = this.battle
      ;(b?.camps || []).forEach((c) => {
        [...(c.pick_heroes || []), ...(c.ban_heroes || [])].forEach(add)
      })
      ;(b?.players || []).forEach((p) => {
        add(p.hero)
        add(p.ban_hero)
      })
      Object.values(this.draft.players).forEach((p) => p.hero_id && add(heroCatalog.byOwnId[p.hero_id]))
      return Object.values(map).filter((h) => this.draft.heroes[h.id])
    },
    entitiesKey() {
      const b = this.battle
      const heroIds = []
      ;(b?.camps || []).forEach((c) => [...(c.pick_heroes || []), ...(c.ban_heroes || [])].forEach((h) => h && heroIds.push(h.id)))
      ;(b?.players || []).forEach((p) => {
        if (p.hero) heroIds.push(p.hero.id)
        if (p.ban_hero) heroIds.push(p.ban_hero.id)
      })
      Object.values(this.draft.players).forEach((p) => p.hero_id && heroIds.push(p.hero_id))
      return [
        (b?.camps || []).map((c) => c.team_id).join(','),
        (b?.players || []).map((p) => p.role_id).join(','),
        heroIds.join(','),
      ].join('|')
    },
  },
  watch: {
    savedKey: {
      handler(key, prev) {
        // чужая трансляция — всегда заново; та же — только если режиссёр ничего не начал править
        const sameBroadcast = prev && key.split(':')[0] === prev.split(':')[0]
        if (!sameBroadcast || !this.dirty) this.reset()
      },
      immediate: true,
    },
    entitiesKey: 'ensureEntities',
  },
  methods: {
    campLogoUrl,
    campShortName,
    campTeamName,

    userAvatar(p) {
      return logoUrl(p.user?.avatar) || ''
    },

    gameHeroLabel(p) {
      const hero = playerHero(p)
      return hero ? `Из игры: ${hero.name}` : 'Из игры (ещё не выбран)'
    },

    reset() {
      this.draft = draftFromOverrides(this.saved)
      this.ensureEntities()
    },

    // поля формы для всех сторон, игроков и героев игры — v-model пишет в готовый реактивный объект
    ensureEntities() {
      const add = (kind, key) => {
        if (key && !this.draft[kind][key]) this.$set(this.draft[kind], key, emptyEntity(kind))
      }
      const b = this.battle
      ;(b?.camps || []).forEach((c) => {
        add('teams', c.team_id)
        ;[...(c.pick_heroes || []), ...(c.ban_heroes || [])].forEach((h) => add('heroes', h?.id))
      })
      ;(b?.players || []).forEach((p) => {
        add('players', p.role_id)
        add('heroes', p.hero?.id)
        add('heroes', p.ban_hero?.id)
      })
      Object.values(this.draft.players).forEach((p) => add('heroes', p.hero_id))
    },

    save() {
      if (this.dirty) this.$emit('save', this.patch)
    },
  },
}
</script>

<style lang="scss">
.mb-ov {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__note, &__hint { margin: 0; line-height: 1.45; }
  &__hint { margin-bottom: 8px; }

  &__section { display: flex; flex-direction: column; gap: 8px; }
  &__title { margin: 0; font-size: 14px; font-weight: 600; }

  &__grid {
    display: grid;
    gap: 12px;

    &--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    &--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    padding: 12px;
    border: 1px solid var(--tl-border);
    border-radius: 10px;
  }

  &__card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 13px;
  }

  &__locked { margin: 0; }

  &__player {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-top: 10px;
    border-top: 1px solid var(--tl-border);
  }

  &__player-head {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  &__player-fields {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
  }

  &__bar {
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 0 4px;
    border-top: 1px solid var(--tl-border);
    background: var(--tl-bg);
  }

  @media (max-width: 1100px) {
    &__grid--2, &__grid--3 { grid-template-columns: 1fr; }
  }
}
</style>
